import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MeterItem, MeterPaginatedResponse, AlertPaginatedResponse, MeterQueryParams, MeterLookupParams, AlertQueryParams, MeterHealthParams, LinkMeterBody } from '../models/meter-manage.model';
import { MeterAbstractService } from './meter.abstract.service';

@Injectable()
export class MeterService extends MeterAbstractService {
    private baseUrl = `${environment.API_GATEWAY_URL}/iot/admin/meters`;

    constructor(private http: HttpClient) {
        super();
    }

    getMeters(params?: MeterQueryParams): Observable<MeterPaginatedResponse> {
        return this.http.get<MeterPaginatedResponse>(this.baseUrl, {
            params: this.buildMeterParams(params),
        });
    }

    lookupMeters(params: MeterLookupParams): Observable<MeterItem[]> {
        let httpParams = new HttpParams();
        if (params.nodeId) {
            httpParams = httpParams.set('nodeId', params.nodeId);
        }
        if (params.address) {
            httpParams = httpParams.set('address', params.address);
        }
        return this.http.get<MeterItem[]>(`${this.baseUrl}/lookup`, { params: httpParams });
    }

    getAlerts(params?: AlertQueryParams): Observable<AlertPaginatedResponse> {
        let httpParams = new HttpParams();
        if (params) {
            if (params.page) {
                httpParams = httpParams.set('page', params.page.toString());
            }
            if (params.limit) {
                httpParams = httpParams.set('limit', params.limit.toString());
            }
            if (params.resolved !== undefined) {
                httpParams = httpParams.set('resolved', params.resolved.toString());
            }
            if (params.from) {
                httpParams = httpParams.set('from', params.from);
            }
            if (params.to) {
                httpParams = httpParams.set('to', params.to);
            }
            if (params.alertType && params.alertType.length > 0) {
                params.alertType.forEach((type) => {
                    httpParams = httpParams.append('alertType', type);
                });
            }
        }
        return this.http.get<AlertPaginatedResponse>(`${this.baseUrl}/alerts`, { params: httpParams });
    }

    getUnassigned(page = 1, limit = 10): Observable<MeterPaginatedResponse> {
        const params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());
        return this.http.get<MeterPaginatedResponse>(`${this.baseUrl}/unassigned`, { params });
    }

    getHealth(params?: MeterHealthParams): Observable<MeterPaginatedResponse> {
        let httpParams = new HttpParams();
        if (params) {
            if (params.page) {
                httpParams = httpParams.set('page', params.page.toString());
            }
            if (params.limit) {
                httpParams = httpParams.set('limit', params.limit.toString());
            }
            if (params.silentDays) {
                httpParams = httpParams.set('silentDays', params.silentDays.toString());
            }
        }
        return this.http.get<MeterPaginatedResponse>(`${this.baseUrl}/health`, { params: httpParams });
    }

    linkMeter(userId: string, body: LinkMeterBody): Observable<any> {
        return this.http.post(`${environment.API_GATEWAY_URL}/iot/admin/${userId}/nodes/link`, body);
    }

    private buildMeterParams(params?: MeterQueryParams): HttpParams {
        let httpParams = new HttpParams();
        if (!params) return httpParams;

        if (params.page) {
            httpParams = httpParams.set('page', params.page.toString());
        }
        if (params.limit) {
            httpParams = httpParams.set('limit', params.limit.toString());
        }
        if (params.status) {
            httpParams = httpParams.set('status', params.status);
        }
        if (params.sortBy) {
            httpParams = httpParams.set('sortBy', params.sortBy);
        }
        if (params.sortOrder) {
            httpParams = httpParams.set('sortOrder', params.sortOrder);
        }
        if (params.nodeType && params.nodeType.length > 0) {
            params.nodeType.forEach((type) => {
                httpParams = httpParams.append('nodeType', type);
            });
        }
        if (params.postalCode && params.postalCode.length > 0) {
            params.postalCode.forEach((code) => {
                httpParams = httpParams.append('postalCode', code);
            });
        }
        return httpParams;
    }
}

export const METER_SERVICE_PROVIDER = {
    provide: MeterAbstractService,
    useClass: MeterService,
};
